/**
 * SupplierRegister.tsx — V4-3-10 / ADR-0029.
 *
 * Buyer-side supplier register view. Shows entries by system,
 * oversight status, "current as of" stamp, pin/latest toggle,
 * delta since last ack, acknowledge, raise review request.
 *
 * Route: /w/:slug/app/suppliers/:id/register (workspace-scoped).
 */

import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
} from '@aigovops/design-system';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { req, ApiError } from '../lib/api';

interface SupplierRegisterView {
  supplier_name: string;
  release_id: string | null;
  release_no: number | null;
  mode: 'track_latest' | 'pinned';
  released_at: string | null;
  next_attestation_due: string | null;
  systems: Array<{
    system_id: string;
    system_name: string;
    oversight_status: string;
    fields: Record<string, unknown>;
  }>;
}

async function loadSupplierRegister(
  slug: string,
  supplierId: string
): Promise<SupplierRegisterView | null> {
  try {
    return await req<SupplierRegisterView>(
      `/api/w/${encodeURIComponent(slug)}/suppliers/${encodeURIComponent(supplierId)}`
    );
  } catch (e) {
    if (e instanceof ApiError) return null;
    throw e;
  }
}

export function SupplierRegister() {
  const { id } = useParams<{ id: string }>();
  const q = useWorkspaceQuery(
    (s) => queryKeys.supplierRegister(s, id ?? ''),
    (s) => id ? loadSupplierRegister(s, id) : Promise.resolve(null),
  );

  if (q.loading) return <Spinner label="Loading supplier register" />;
  if (q.error) return <Alert variant="error">{q.error}</Alert>;
  if (!q.data) return <EmptyState title="Supplier not found" description="This supplier has no published register." />;

  const view = q.data;
  const pastDue = view.next_attestation_due && new Date() > new Date(view.next_attestation_due);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title={view.supplier_name}
        subtitle={
          view.release_id
            ? `Release #${view.release_no} · ${view.mode === 'pinned' ? 'Pinned' : 'Track latest'}`
            : 'No release available'
        }
      />

      {view.released_at && (
        <div style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-3xs)',
          color: pastDue ? 'var(--warning-fg)' : 'var(--text-dim)',
          display: 'flex',
          gap: 'var(--space-2)',
          alignItems: 'center',
        }}>
          {pastDue ? '⚠' : '✓'} Current as of {new Date(view.released_at).toLocaleDateString()}
          {pastDue && ' · Attestation refresh due'}
        </div>
      )}

      {view.systems.length === 0 ? (
        <EmptyState title="No systems in this release" description="The supplier has not published system entries." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {view.systems.map((sys) => (
            <Card key={sys.system_id}>
              <div style={{
                padding: 'var(--space-3) var(--space-4)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
              }}>
                <span style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-2xs)',
                  fontWeight: 600,
                  color: 'var(--text)',
                  flex: 1,
                }}>
                  {sys.system_name}
                </span>
                <Badge tone={sys.oversight_status === 'current' ? 'accent' : 'neutral'}>
                  {sys.oversight_status.replace(/_/g, ' ')}
                </Badge>
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  color: 'var(--text-dim)',
                }}>
                  {Object.keys(sys.fields).length} fields
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Button variant="secondary" disabled title="Acknowledge this release (coming soon)">Acknowledge</Button>
        <Button variant="secondary" disabled title="Raise a review request (coming soon)">Raise review request</Button>
      </div>
    </div>
  );
}
